/**
 * Several values from a fixed list, for a request field (an array of an enum) or a dry run's multiple choice. A
 * request field draws a checkbox per option while the list is short and a searchable picker with the picks as
 * removable chips when it is long (`multiSelectModeOf`). A dry run's choice (`variant="cards"`) always draws its
 * cards, with the facts and reasons each option carries, and a search box over them when the list is long, as a single
 * choice does. A disabled option shows why.
 */
import { useState } from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import type { ChoiceOption } from "@shared-core/domain/object-ui/contract";
import { multiSelectModeOf } from "@shared-core/domain/object-ui/widgets";
import { Button } from "../../primitives/button";
import { Checkbox } from "../../primitives/checkbox";
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "../../primitives/command";
import { Label } from "../../primitives/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../primitives/popover";
import { cn } from "../../primitives/utils";
import { useChoiceSearch, type RemoteSearch } from "./ChoiceSearch";

export function MultiSelect({
  id,
  label,
  options,
  value,
  disabled,
  variant = "plain",
  invalid = {},
  remoteSearch,
  onChange,
}: {
  /** The DOM id prefix for the options' inputs. */
  id: string;
  /** Names the group and the picker for assistive tech. */
  label: string;
  options: readonly ChoiceOption[];
  value: readonly string[];
  disabled: boolean;
  /** `cards`: a dry run's choice, each option a bordered card with its facts, as a single choice draws. */
  variant?: "plain" | "cards";
  /** `aria-invalid` / `aria-describedby` from the field's errors. */
  invalid?: Record<string, unknown>;
  /** A searchable dry-run choice: the owner answers the search box, which is always drawn (cards only). */
  remoteSearch?: RemoteSearch;
  onChange: (values: string[]) => void;
}) {
  const isPicked = (option: ChoiceOption) => value.includes(option.value);
  // Kept in the options' order, whatever order they were ticked in.
  const toggle = (option: ChoiceOption, on: boolean) =>
    onChange(
      options
        .filter((o) =>
          o.value === option.value ? on : value.includes(o.value),
        )
        .map((o) => o.value),
    );

  if (variant === "plain" && multiSelectModeOf(options.length) === "search")
    return (
      <SearchPicker
        label={label}
        options={options}
        value={value}
        disabled={disabled}
        invalid={invalid}
        isPicked={isPicked}
        toggle={toggle}
      />
    );

  return (
    <CheckboxList
      id={id}
      label={label}
      options={options}
      disabled={disabled}
      variant={variant}
      invalid={invalid}
      remoteSearch={remoteSearch}
      isPicked={isPicked}
      toggle={toggle}
    />
  );
}

function CheckboxList({
  id,
  label,
  options,
  disabled,
  variant,
  invalid,
  remoteSearch,
  isPicked,
  toggle,
}: {
  id: string;
  label: string;
  options: readonly ChoiceOption[];
  disabled: boolean;
  variant: "plain" | "cards";
  invalid: Record<string, unknown>;
  remoteSearch?: RemoteSearch;
  isPicked: (option: ChoiceOption) => boolean;
  toggle: (option: ChoiceOption, on: boolean) => void;
}) {
  // A long plain list never reaches here (it is the picker), so only cards ever get the search box.
  const { shown, searchBox } = useChoiceSearch({
    label,
    options,
    disabled,
    isKept: isPicked,
    remote: remoteSearch,
  });
  return (
    <div>
      {searchBox}
      <div role="group" aria-label={label} className="space-y-1.5" {...invalid}>
        {shown.map((option) => {
          const optionId = `${id}-${option.value}`.replace(
            /[^a-zA-Z0-9_-]/g,
            "-",
          );
          const isDisabled = disabled || !!option.disabled_reason;
          const note = option.disabled_reason ?? option.why;
          return (
            <Label
              key={option.value}
              htmlFor={optionId}
              className={cn(
                "flex items-start gap-2 font-normal",
                variant === "cards" &&
                  "cursor-pointer gap-3 rounded-md border border-border px-3 py-2",
                variant === "cards" &&
                  isPicked(option) &&
                  "border-primary bg-primary/5",
                option.disabled_reason &&
                  "cursor-not-allowed text-muted-foreground",
              )}
            >
              <Checkbox
                id={optionId}
                checked={isPicked(option)}
                disabled={isDisabled}
                className="mt-0.5"
                onCheckedChange={(checked) => toggle(option, checked === true)}
              />
              <span className="flex min-w-0 flex-col">
                <span className="flex flex-wrap items-center gap-2">
                  {option.label}
                  <OptionTag tag={option.tag} />
                </span>
                {note && (
                  <span className="text-xs text-muted-foreground">{note}</span>
                )}
              </span>
              {option.facts && (
                <span className="ml-auto text-right text-sm tabular-nums sm:whitespace-nowrap">
                  {option.facts}
                </span>
              )}
            </Label>
          );
        })}
      </div>
    </div>
  );
}

/** A small label on a card: what the option is ("Matched now", "Suggested"), not a heading over a group. */
export function OptionTag({ tag }: { tag: string | null | undefined }) {
  return tag ? (
    <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
      {tag}
    </span>
  ) : null;
}

function SearchPicker({
  label,
  options,
  value,
  disabled,
  invalid,
  isPicked,
  toggle,
}: {
  label: string;
  options: readonly ChoiceOption[];
  value: readonly string[];
  disabled: boolean;
  invalid: Record<string, unknown>;
  isPicked: (option: ChoiceOption) => boolean;
  toggle: (option: ChoiceOption, on: boolean) => void;
}) {
  const [isOpen, setOpen] = useState(false);
  const picked = options.filter(isPicked);
  return (
    <div className="space-y-1.5">
      {picked.length > 0 && (
        <ul className="flex flex-wrap gap-1.5" aria-label={label}>
          {picked.map((option) => (
            <li
              key={option.value}
              className="flex items-center gap-1 rounded-md border bg-muted px-2 py-0.5 text-sm"
            >
              {option.label}
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-5 w-5"
                disabled={disabled}
                aria-label={`Remove ${option.label}`}
                onClick={() => toggle(option, false)}
              >
                <X className="h-3 w-3" />
              </Button>
            </li>
          ))}
        </ul>
      )}
      <Popover open={isOpen} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={isOpen}
            aria-label={label}
            disabled={disabled}
            className="w-full justify-between font-normal"
            {...invalid}
          >
            {value.length === 0 ? "Choose…" : `${value.length} chosen`}
            <ChevronsUpDown className="size-4 opacity-50" aria-hidden />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[--radix-popover-trigger-width] p-0"
          align="start"
        >
          <Command>
            <CommandInput placeholder={`Search ${label.toLowerCase()}…`} />
            <CommandList>
              <CommandEmpty>No options match.</CommandEmpty>
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.label}
                  disabled={!!option.disabled_reason}
                  onSelect={() => toggle(option, !isPicked(option))}
                >
                  <Check
                    className={cn(
                      "size-4",
                      isPicked(option) ? "opacity-100" : "opacity-0",
                    )}
                    aria-hidden
                  />
                  <span className="flex min-w-0 flex-col">
                    <span>{option.label}</span>
                    {option.disabled_reason && (
                      <span className="text-xs text-muted-foreground">
                        {option.disabled_reason}
                      </span>
                    )}
                  </span>
                </CommandItem>
              ))}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
